import { useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Bookmark, Trash2, Plus, Loader2 } from "lucide-react";

export const SnippetManager = ({ open, onOpenChange, snippets, onChanged }) => {
  const [label, setLabel] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!label.trim() || !body.trim()) return toast.error("Add a label and text");
    setSaving(true);
    try { await api.post("/admin/my-snippets", { label: label.trim(), body: body.trim() }); setLabel(""); setBody(""); toast.success("Snippet added"); onChanged(); }
    catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/my-snippets/${id}`); toast.success("Snippet removed"); onChanged(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="snippet-manager">
        <DialogHeader>
          <DialogTitle>Your personal snippets</DialogTitle>
          <DialogDescription>Private quick-insert phrases, only visible to you. Use <code className="text-[#F5551E]">{"{first}"}</code> for the sender's first name. Click a snippet in the composer to append it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {snippets.length === 0 && <p className="text-sm text-slate-400">No snippets yet — add your first below.</p>}
          {snippets.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 p-3 flex items-start justify-between gap-3" data-testid={`snippet-item-${s.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-700 flex items-center gap-1"><Bookmark size={13} /> {s.label}</p>
                <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{s.body}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`snippet-delete-${s.id}`} className="rounded-lg h-8 text-red-500 hover:text-red-600 gap-1 shrink-0"><Trash2 size={14} /></Button>
            </div>
          ))}
          <div className="rounded-xl border border-dashed border-slate-300 p-3" data-testid="snippet-add">
            <p className="text-xs font-semibold text-slate-500 mb-2">Add a new snippet</p>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Refund policy)" data-testid="snippet-new-label" className="rounded-lg h-9" maxLength={40} />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Snippet text… use {first} for the sender's name" data-testid="snippet-new-body" className="rounded-lg mt-2 min-h-[80px] text-sm" maxLength={2000} />
            <Button size="sm" onClick={add} disabled={saving} data-testid="snippet-new-add" className="rounded-lg h-8 mt-2 gap-1">{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add snippet</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
